import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Package,
  ShoppingBag,
  Users,
  Tag,
  LayoutDashboard,
  LogOut,
  Image,
  Mail,
  FileText,
  Briefcase,
  Calendar,
  Sparkles,
  Menu,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Analytics } from '@vercel/analytics/react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const navItems = [
  { title: 'Overview', icon: LayoutDashboard, path: '/admin', end: true },
  { title: 'Products', icon: Package, path: '/admin/products' },
  { title: 'Materials', icon: Tag, path: '/admin/materials' },
  { title: 'Textiles', icon: Image, path: '/admin/textiles' },
  { title: 'Orders', icon: ShoppingBag, path: '/admin/orders' },
  { title: 'Categories', icon: Tag, path: '/admin/categories' },
  { title: 'Users', icon: Users, path: '/admin/users' },
  { title: 'Appointments', icon: Calendar, path: '/admin/appointments' },
  { title: 'Events', icon: Sparkles, path: '/admin/events' },
  { title: 'Careers', icon: Briefcase, path: '/admin/careers' },
  { title: 'Hero Sections', icon: Image, path: '/admin/hero' },
  { title: 'Newsletter', icon: Mail, path: '/admin/newsletter' },
  { title: 'Partners', icon: Briefcase, path: '/admin/partners' },
  { title: 'Devis', icon: FileText, path: '/admin/estimates' },
] as const;

const AdminDashboard = () => {
  const { signOut, profile, isAdmin, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    console.log('AdminDashboard: User:', user);
    console.log('AdminDashboard: Profile:', profile);
    console.log('AdminDashboard: Is Admin:', isAdmin);
  }, [user, profile, isAdmin]);

  useEffect(() => {
    if (profile && !isAdmin) {
      toast({
        title: 'Access Denied',
        description: "You don't have permission to access the admin dashboard.",
        variant: 'destructive',
      });
      navigate('/');
    }
  }, [profile, isAdmin, navigate, toast]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (!isAdmin) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="mb-4 text-2xl font-bold">Access Denied</h1>
        <p className="mb-6">You don't have permission to access the admin dashboard.</p>
        <Link to="/" className="btn-primary">
          Return to Home
        </Link>
      </div>
    );
  }

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({ title: 'Signed out successfully' });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const currentTitle =
    navItems.find((item) =>
      item.end ? location.pathname === item.path : location.pathname.startsWith(item.path)
    )?.title || 'Admin';

  const NavBody = ({ onNavigate }: { onNavigate?: () => void }) => (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border/60 px-5 py-5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-furniture-gold">
          Meubles Karim
        </p>
        <h2 className="font-sans text-xl font-semibold text-furniture-brown">Admin Panel</h2>
      </div>

      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-3 py-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={'end' in item ? item.end : false}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-furniture-gold/15 text-foreground shadow-soft'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span>{item.title}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="shrink-0 border-t border-border/60 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-2 truncate px-1 text-xs text-muted-foreground">
          Connecté : <span className="font-medium text-foreground">{profile?.email}</span>
        </div>
        <Button
          variant="ghost"
          className="h-11 w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={handleSignOut}
        >
          <LogOut className="mr-2 h-5 w-5" />
          Déconnexion
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-[100dvh] bg-[#f7f4ef]">
      <Helmet>
        <title>Admin Dashboard | Meubles Karim</title>
      </Helmet>

      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-[100dvh] w-64 shrink-0 flex-col border-r border-border/70 bg-white text-black shadow-sm md:flex">
        <NavBody />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          className="w-[min(100vw,20rem)] border-r border-border/70 bg-white p-0 text-black [&>button]:right-3 [&>button]:top-3"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation admin</SheetTitle>
            <SheetDescription>Menu de navigation du tableau de bord</SheetDescription>
          </SheetHeader>
          <NavBody onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border/70 bg-white/95 px-3 backdrop-blur supports-[padding:max(0px)]:pt-[env(safe-area-inset-top)] md:hidden">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 shrink-0"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[10px] font-semibold uppercase tracking-[0.18em] text-furniture-gold">
              Admin
            </p>
            <h1 className="truncate font-sans text-base font-semibold leading-tight">{currentTitle}</h1>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-auto bg-white px-3 py-4 text-black sm:px-4 md:px-6 md:py-6">
          <Outlet />
        </main>
      </div>

      <Analytics />
    </div>
  );
};

export default AdminDashboard;
